import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CONTACT_INFO } from "../constants";
import { Button } from "./Button";

const NAV_LINKS = [
  { name: "Ana Sayfa", href: "/" },
  { name: "Hizmetler", href: "/services" },
  { name: "Hakkımızda", href: "/about" },
  { name: "Ekibimiz", href: "/team" },
  { name: "Galeri", href: "/gallery" },
  { name: "İletişim", href: "/contact" },
];

export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-primary-dark/95 backdrop-blur-md py-4 shadow-lg"
          : "bg-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 md:px-6 relative z-50">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex flex-col items-start group relative z-50"
          >
            <div className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-wider">
              <span className="text-white">SALOON</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-gold to-accent-copper ml-1 sm:ml-2">
                FENOMEN
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm uppercase tracking-wider font-medium transition-colors relative group ${
                  location.pathname === link.href
                    ? "text-accent-gold"
                    : "text-white/80 hover:text-accent-gold"
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-2 left-0 h-0.5 bg-accent-gold transition-all duration-300 ${
                    location.pathname === link.href
                      ? "w-full"
                      : "w-0 group-hover:w-full"
                  }`}
                ></span>
              </Link>
            ))}
            <Button
              href={`https://api.whatsapp.com/send?phone=${CONTACT_INFO.whatsapp}&text=Merhaba,%20randevu%20almak%20istiyorum.`}
              variant="primary"
              className="!py-2 !px-6 text-xs"
              trackingLabel="Header - Randevu Al"
            >
              Randevu Al
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden text-white text-xl sm:text-2xl focus:outline-none relative z-50 w-10 h-10 flex items-center justify-center touch-manipulation active:scale-95 transition-transform"
            aria-label={isMobileMenuOpen ? "Menüyü Kapat" : "Menüyü Aç"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <i className="fas fa-times text-accent-gold"></i>
            ) : (
              <i className="fas fa-bars"></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Fullscreen Menu Overlay */}
      <div
        className={`fixed inset-0 bg-primary-dark z-40 flex flex-col items-center justify-center transition-all duration-500 ${
          isMobileMenuOpen
            ? "opacity-100 visible translate-x-0"
            : "opacity-0 invisible translate-x-full"
        }`}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-accent-gold to-transparent"></div>

        <div className="flex flex-col items-center gap-6 sm:gap-8 p-6 sm:p-8 w-full relative z-10 overflow-y-auto max-h-screen">
          {NAV_LINKS.map((link, index) => (
            <Link
              key={link.name}
              to={link.href}
              className={`text-xl sm:text-2xl md:text-3xl font-display font-bold transition-all duration-300 transform touch-manipulation ${
                location.pathname === link.href
                  ? "text-accent-gold"
                  : "text-white hover:text-accent-gold active:text-accent-copper"
              } ${
                isMobileMenuOpen
                  ? "translate-y-0 opacity-100"
                  : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {link.name}
            </Link>
          ))}
          <div
            className={`mt-6 sm:mt-8 transition-all duration-500 delay-500 px-4 ${
              isMobileMenuOpen
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            }`}
          >
            <Button
              href={`https://api.whatsapp.com/send?phone=${CONTACT_INFO.whatsapp}&text=Merhaba,%20randevu%20almak%20istiyorum.`}
              variant="primary"
              className="w-full min-w-[200px] text-sm sm:text-base"
              trackingLabel="Mobile Menu - WhatsApp Randevu"
            >
              WhatsApp ile Randevu
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
